/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { useDashboardContext } from "../../../../context/dashboard/context/DashboardContext";
import { useAdminContext } from "../../../../context/admin/context/AdminContext";

const TableStats = () => {
  const { sysAdmin } = useAdminContext();
  const { table, getTableData } = useDashboardContext();
  const [localTable, setLocalTable] = useState(null);

  useEffect(() => {
    if (!table) getTableData();
    else setLocalTable(table);
  }, [table]);

  const canSeeLevels = ["super_admin", "admin"].includes(sysAdmin?.role);

  const REGIONS = [
    { title: "East Africa", path: "EastAfrica" },
    { title: "Central Africa", path: "CentralAfrica" },
    { title: "North Africa", path: "NorthAfrica" },
    { title: "West Africa", path: "WestAfrica" },
    { title: "Southern Africa", path: "SouthernAfrica" },
  ];

  const LEVELS = canSeeLevels
    ? [
      { key: "country", label: "Countries" },
      { key: "first_level", label: "First/Levels" },
      { key: "second_level", label: "Second/Levels" },
      { key: "third_level", label: "Third/Levels" },
      { key: "fourth_level", label: "Fourth/Levels" },
    ]
    : [];

  const CONTENT = [
    { key: "event", label: "Events" },
    { key: "attraction", label: "Attractions" },
    { key: "news", label: "News" },
    { key: "museum", label: "Museums" },
    { key: "restaurant", label: "Restaurants" },
    { key: "nightlife", label: "Night/Life" },
    { key: "hotel", label: "Hotels" },
    { key: "class", label: "Classes" },
    { key: "shopping", label: "Shopping/Centers" },
    { key: "food", label: "Foods" },
    { key: "book", label: "Books" },
    { key: "neighborhood", label: "Neighborhoods" },
    { key: "art", label: "Arts" },
    { key: "movie", label: "Movies" },
    { key: "dance", label: "Dances" },
    { key: "theater", label: "Theaters" },
    { key: "best_of_africa", label: "Best/Of/Africa" },
    { key: "cinema", label: "Cinema" },
    { key: "city_guide", label: "City/Guide" },
    { key: "getting_around", label: "Getting/Around" },
    { key: "ministry", label: "Ministries" },
    { key: "music", label: "Musics" },
    { key: "party", label: "Parties" },
    { key: "safari", label: "Safaris" },
    { key: "things_to_do", label: "Things/ToDo" },
    { key: "tips_and_advice", label: "Tips/And/Advices" },
    { key: "tour_package", label: "Tour/Packages" },
    { key: "product", label: "Products" },
  ];

  const TONES = [
    "from-emerald-400/20 via-teal-400/20 to-cyan-400/20",
    "from-sky-400/20 via-blue-400/20 to-indigo-400/20",
    "from-amber-400/20 via-orange-400/20 to-yellow-400/20",
    "from-rose-400/20 via-pink-400/20 to-fuchsia-400/20",
    "from-lime-400/20 via-green-400/20 to-emerald-400/20",
  ];

  const format = (v) =>
    typeof v === "number" && Number.isFinite(v) ? v.toLocaleString() : 0;

  const RegionShell = ({ tone, title, children }) => (
    <div className="relative">
      <div className={`absolute -inset-[1px] rounded-3xl bg-gradient-to-r ${tone} blur-2xl`} />
      <div className="relative rounded-3xl border border-gray-200 bg-white/80 shadow-sm backdrop-blur">
        <div className="flex items-center justify-between px-5 pt-4">
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 shrink-0 rounded-xl bg-gradient-to-br from-gray-900 to-gray-700 text-white grid place-items-center text-xs font-bold shadow">
              {title?.slice(0, 2).toUpperCase()}
            </div>
            <div>
              <h4 className="text-sm font-semibold tracking-tight text-gray-900">{title}</h4>
              <p className="text-[11px] text-gray-500">Live content footprint</p>
            </div>
          </div>
          <div className="hidden sm:block h-1 w-28 rounded bg-gradient-to-r from-gray-900 via-gray-700 to-gray-900 opacity-30" />
        </div>
        <div className="px-5 pb-5 pt-4">{children}</div>
      </div>
    </div>
  );

  const SectionLabel = ({ children }) => (
    <div className="mb-2 flex items-center gap-2">
      <span className="rounded-full bg-gray-900/90 px-2.5 py-1 text-[10px] font-semibold text-white shadow">
        {children}
      </span>
      <span className="h-[1px] flex-1 bg-gray-200" />
    </div>
  );

  const StatPill = ({ label, value }) => (
    <div
      className={[
        "group relative overflow-hidden rounded-2xl border border-gray-200",
        "bg-gradient-to-br from-white to-gray-50/70",
        "shadow-sm hover:shadow-md transition duration-200 ease-out",
        "hover:-translate-y-[1px]",
      ].join(" ")}
    >
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-tr from-gray-900/[0.05] via-transparent to-transparent opacity-0 transition-opacity duration-200 group-hover:opacity-100" />
      <div className="flex items-center justify-between px-3 py-2.5">
        <span className="truncate pr-2 text-[11px] font-medium text-gray-700">
          {label}
        </span>
        <span className="rounded-lg bg-gray-900 px-2.5 py-1 text-[11px] font-semibold text-white shadow-sm">
          {format(value)}
        </span>
      </div>
    </div>
  );

  return (
    <section className="mt-8 grid grid-cols-1 gap-6">
      {REGIONS.map(({ title, path }, i) => {
        const data = localTable?.[path];
        const tone = TONES[i % TONES.length];
        return (
          <RegionShell key={title} tone={tone} title={title}>
            {LEVELS.length > 0 && (
              <>
                <SectionLabel>Levels</SectionLabel>
                <div className="mb-3 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
                  {LEVELS.map((c) => (
                    <StatPill key={c.key} label={c.label} value={data?.[c.key]} />
                  ))}
                </div>
              </>
            )}

            <SectionLabel>Content</SectionLabel>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5 2xl:grid-cols-7">
              {CONTENT.map((c) => (
                <StatPill key={c.key} label={c.label} value={data?.[c.key]} />
              ))}
            </div>
          </RegionShell>
        );
      })}
    </section>
  );
};

export default TableStats;
